import type { AccountingHealth, MappingIssue } from "@/api/accounting";
import { Link } from "react-router-dom";

type Props = {
  open: boolean;
  health: AccountingHealth | null;
  onClose: () => void;
  ctaHref?: string;
  ctaLabel?: string;
};

type IssueGroup = {
  title: string;
  items: Array<{ label: string; issues: string[]; subtitle?: string }>;
};

export function MappingHealthModal({ open, health, onClose, ctaHref, ctaLabel }: Props) {
  if (!open || !health || health.ok) {
    return null;
  }

  const groups: IssueGroup[] = [];
  if (health.missing.length) {
    groups.push({
      title: "Required ledger accounts",
      items: health.missing.map((item) => ({ label: item, issues: [] })),
    });
  }
  if (Object.keys(health.bank).length) {
    groups.push({
      title: "Bank accounts",
      items: mapIssues(health.bank),
    });
  }
  if (Object.keys(health.shopify).length) {
    groups.push({
      title: "Shopify shops",
      items: mapIssues(health.shopify),
    });
  }
  if (Object.keys(health.events).length) {
    groups.push({
      title: "Events",
      items: mapIssues(health.events, (issue) =>
        issue.event_date ? new Date(issue.event_date).toLocaleDateString() : undefined,
      ),
    });
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 px-4">
      <div className="max-h-[90vh] w-full max-w-2xl space-y-5 overflow-y-auto rounded-2xl border border-border bg-background/85 p-6 shadow-2xl">
        <header className="flex items-center justify-between gap-3">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-destructive">Accounting health</p>
            <h2 className="text-lg font-semibold text-foreground">Mapping requirements</h2>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-border px-3 py-1 text-xs uppercase tracking-wide text-foreground/70 hover:text-foreground"
          >
            Close
          </button>
        </header>
        {groups.length === 0 ? (
          <p className="text-sm text-foreground/70">Mappings look good.</p>
        ) : (
          <div className="space-y-4">
            {groups.map((group) => (
              <section key={group.title} className="rounded-xl border border-border/80 p-4">
                <h3 className="text-sm font-semibold text-foreground">{group.title}</h3>
                <ul className="mt-3 space-y-3 text-sm text-foreground/80">
                  {group.items.map((item) => (
                    <li key={item.label}>
                      <p className="font-semibold text-primary-foreground dark:text-amber-100">{item.label}</p>
                      {item.subtitle && <p className="text-xs text-muted-foreground">{item.subtitle}</p>}
                      {item.issues.length ? (
                        <ul className="mt-1 list-disc space-y-1 pl-4 text-xs text-foreground/70">
                          {item.issues.map((issue) => (
                            <li key={issue}>{issue}</li>
                          ))}
                        </ul>
                      ) : (
                        <p className="text-xs text-muted-foreground">Missing mapping</p>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        )}
        {ctaHref && (
          <div className="flex justify-end">
            <Link
              to={ctaHref}
              className="rounded-full border border-primary/60 px-4 py-1.5 text-xs font-semibold uppercase tracking-wide text-primary transition hover:border-primary hover:text-foreground"
              onClick={onClose}
            >
              {ctaLabel ?? "Open Accounting Setup"}
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

function mapIssues(
  collection: Record<string, MappingIssue>,
  subtitle?: (issue: MappingIssue) => string | undefined,
) {
  return Object.values(collection).map((issue) => ({
    label: issue.label,
    issues: issue.issues,
    subtitle: subtitle ? subtitle(issue) : undefined,
  }));
}
